import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Drawer, message, theme, Select, TreeSelect } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import CSVImporter from '../../../../common/CSVImporter';
import { useUserCreate } from '../hooks/use-user.hook';
import { extractValidationError } from '../../../../../services/extract-api-error.service';
import { ValidationError, ApiResponseWithSummary } from '../../../../../types/api-response.types';
import { User } from '../types/user.types';
import ValidationErrorsPreview from '../../../../common/ValidationErrorsPreview';
import { FailedRecord, Summary } from '../../../../common/BulkImportSummaryPreview';
import SelectField from '../../../../common/SelectField';
import { useRoleList } from '../../roles/hooks/use-role.hook';
import { Auth0IdentityProvider } from '@gluai-studio/passport';
import { Role } from '../../roles/types/role.types';
import { PaginatedData, UseListParams } from '../../../../../types/pagination.types';
import { SelectValue } from 'antd/es/select';
import FormBuilder, { FormField } from '../../../../common/FormBuilder';

interface UserBulkImportContainerProps {
  visible: boolean;
  onClose: () => void;
}

interface BulkImportFormData {
  role: string;
  userType: Auth0IdentityProvider;
}

const UserBulkImportContainer: React.FC<UserBulkImportContainerProps> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [failedRecords, setFailedRecords] = useState<FailedRecord<User>[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState<Summary>({
    successful: 0,
    failed: 0,
    total: 0,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<BulkImportFormData>({
    defaultValues: {
      role: '',
      userType: Auth0IdentityProvider.DATABASE,
    },
  });

  const selectedRole = watch('role');
  const selectedUserType = watch('userType');

  const handleCloseDrawer = useCallback(() => {
    onClose();
  }, [onClose]);

  const { mutateAsync: createUser, isPending } = useUserCreate();

  const handleBulkCreateUser = useCallback(
    async (csvData: any) => {
      try {
        // Add role and user type to each record
        const enrichedData = csvData.map((record: any) => ({
          ...record,
          role: selectedRole,
          userType: selectedUserType,
        }));

        const response = await createUser(enrichedData);
        setSummary((response.data as unknown as ApiResponseWithSummary<User>).summary);
        setFailedRecords((response as unknown as ApiResponseWithSummary<User>).data.failed);
        setValidationErrors([]);
        setShowSummary(true);
        message.success(t('crud.create.success', { entity: t('employees.title') }));
      } catch (error) {
        console.log(error);
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        message.error(t('crud.create.error', { entity: t('employees.title') }));
      }
    },
    [createUser, t, selectedRole, selectedUserType]
  );

  const formFields: FormField<BulkImportFormData>[] = [
    {
      name: 'role',
      label: t('users.role.label'),
      required: true,
      errorMsg: errors?.role?.message,
      render: (field) => (
        <SelectField
          {...field}
          disabled={isPending}
          placeholder={t('users.role.placeholder')}
          useListHook={
            useRoleList as unknown as (params: UseListParams) => {
              data?: PaginatedData<Record<string, unknown>>;
              isLoading: boolean;
              isFetching: boolean;
            }
          }
          labelKey="name"
          valueKey="id"
        />
      ),
    },
    {
      name: 'userType',
      label: t('users.userType.label'),
      required: true,
      errorMsg: errors?.userType?.message,
      render: (field) => (
        <Select
          {...field}
          disabled={isPending}
          placeholder={t('users.userType.placeholder')}
          className="w-100"
        >
          <Select.Option value={Auth0IdentityProvider.DATABASE}>
            {t('users.userType.database')}
          </Select.Option>
          <Select.Option value={Auth0IdentityProvider.GOOGLE}>
            {t('users.userType.google')}
          </Select.Option>
        </Select>
      ),
    },
  ];

  return (
    <Drawer
      title={t('crud.create.bulkImport')}
      placement="right"
      onClose={handleCloseDrawer}
      open={visible}
      width={600}
    >
      <FormBuilder
        showSubmitButton={false}
        formFields={formFields}
        control={control}
        errors={errors}
        onSubmit={() => {}} // We don't need a submit handler as we're using the form values in CSV import
        submitButtonText={t('common.save')}
        isLoading={isPending}
        columns={2}
      />

      <CSVImporter
        mappingStructure={{
          name: 'Name',
          email: 'Email',
          contact: 'Contact',
        }}
        uploadConfig={{
          allowedFileTypes: ['text/csv', 'application/vnd.ms-excel'],
          maxFileSize: 50 * 1024 * 1024, // 5MB
          multiple: false,
        }}
        onComplete={handleBulkCreateUser}
        isLoading={isPending}
        summary={summary}
        showSummary={showSummary}
        failedRecords={failedRecords}
      />

      {validationErrors.length > 0 && (
        <div className="row mt-3">
          <ValidationErrorsPreview
            validationErrors={validationErrors}
            entity={t('employees.title')}
          />
        </div>
      )}
    </Drawer>
  );
};

export default UserBulkImportContainer;
